import React, { useState } from "react";
import { Copy, Check } from "lucide-react";
import { shortHash } from "../../lib/format";
import { cn } from "../../lib/utils";

function CopyButton({ value, testId }) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch {
          // ignored
        }
      }}
      aria-label={`Copy ${value}`}
      data-testid={testId}
      className="inline-flex items-center rounded p-0.5 text-slate-500 hover:bg-slate-200 hover:text-slate-900 transition"
    >
      {copied ? <Check size={12} /> : <Copy size={12} />}
    </button>
  );
}

function HashCell({ label, value, testId }) {
  return (
    <div className="flex items-baseline gap-2 min-w-0">
      <span className="text-[10px] uppercase tracking-wider text-slate-500 shrink-0">
        {label}
      </span>
      <span
        className="font-mono text-xs text-slate-800 truncate"
        title={value || "—"}
        data-testid={testId}
      >
        {shortHash(value)}
      </span>
      {value && <CopyButton value={value} testId={`${testId}-copy`} />}
    </div>
  );
}

/**
 * AuditStrip — the six-field integrity band shown on run detail.
 *
 * Displays rule_library_hash, rule_library_version, engine_version,
 * mapping_content_hash (or a fallback label if the run was JSON-ingested),
 * mapping_profile_version, dataset_id.
 */
export function AuditStrip({ run, className }) {
  if (!run) return null;
  const isJsonIngest = !run.mapping_content_hash;
  return (
    <div
      data-testid="audit-strip"
      className={cn(
        "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2 rounded border border-slate-200 bg-slate-50/60 px-4 py-3",
        className,
      )}
    >
      <HashCell
        label="Engine"
        value={run.engine_version}
        testId="audit-engine-version"
      />
      <HashCell
        label="Rule library"
        value={run.rule_library_version}
        testId="audit-rule-library-version"
      />
      <HashCell
        label="Rule library hash"
        value={run.rule_library_hash}
        testId="audit-rule-library-hash"
      />
      <HashCell
        label="Dataset id"
        value={run.dataset_id}
        testId="audit-dataset-id"
      />
      <HashCell
        label="Mapping version"
        value={run.mapping_profile_version || (isJsonIngest ? "json_ingest" : null)}
        testId="audit-mapping-version"
      />
      <HashCell
        label="Mapping hash"
        value={run.mapping_content_hash || (isJsonIngest ? "json_ingest_no_mapping" : null)}
        testId="audit-mapping-hash"
      />
    </div>
  );
}
